'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendWholesalerContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { register, updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { useAuthNavigation } from '@eliadmin/core/src/components/dlz/auth/dialog/auth-navigation';
import { BillingCard } from '@eliadmin/core/src/components/dlz/auth/register/billing-card';
import { CompanyCard } from '@eliadmin/core/src/components/dlz/auth/register/company-card';
import { ContactCard } from '@eliadmin/core/src/components/dlz/auth/register/contact-card';
import { Consents } from '@eliadmin/core/src/components/dlz/auth/register/consents';
import { CredentialsCard } from '@eliadmin/core/src/components/dlz/auth/register/credentials-card';
import { DeliveryCard } from '@eliadmin/core/src/components/dlz/auth/register/delivery-card';
import { PersonalCard } from '@eliadmin/core/src/components/dlz/auth/register/personal-card';
import {
  RegisterLayoutProvider,
  type RegisterLayout,
} from '@eliadmin/core/src/components/dlz/auth/register/register-card';
import { PersonaCard } from '@eliadmin/core/src/components/dlz/auth/register/persona-card';
import {
  SERVER_FIELD_MAP,
  toBusinessAccountRequest,
  toCustomerUpdate,
  toRegisterBody,
} from '@eliadmin/core/src/components/dlz/auth/register/register-payload';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import {
  registerFormSchema,
  type RegisterFormInput,
  type RegisterFormValues,
} from '@eliadmin/core/src/components/dlz/auth/register/register-schema';
import { defaultCountryId } from '@eliadmin/core/src/components/dlz/auth/register/register-values';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

type Session = { authToken: string; customer: Customer };

/**
 * Registration (Figma 60260:27827 / 60260:28858). `register` takes the account fields only; the
 * address, company and delivery data follow in one `updateCustomer` with the field names the
 * checkout and the profile read, so they prefill identically. A failing second step never undoes
 * the account: the customer is told, then sees the confirmation.
 */
export function RegisterForm({
  countries,
  defaultValues,
  layout = 'page',
}: {
  countries: Country[];
  /** `page` = the Figma cards of the registration page, `dialog` = flat sections in the dialog card. */
  layout?: RegisterLayout;
  /** Harness previews only: starting values of the form. */
  defaultValues?: Partial<RegisterFormInput>;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);

  const { toast } = useToast();
  const { showRegistered } = useAuthNavigation();

  const defaultCountry = defaultCountryId(countries);
  const form = useForm<RegisterFormInput, unknown, RegisterFormValues>({
    resolver: zodResolver(registerFormSchema(formValidationMessages)),
    defaultValues: {
      persona: 'individual',
      email: '',
      password: '',
      password_confirmation: '',
      name: '',
      surname: '',
      phone: '',
      contact_email: '',
      address: '',
      city: '',
      postcode: '',
      country: defaultCountry,
      company: '',
      ico: '',
      dic: '',
      ic_dph: '',
      different_delivery_address: false,
      delivery_company: '',
      delivery_contact: '',
      delivery_address: '',
      delivery_city: '',
      delivery_postcode: '',
      delivery_country: defaultCountry,
      consent: false,
      business_account: false,
      newsletter: false,
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
      ...defaultValues,
    },
  });
  const persona = useWatch({ control: form.control, name: 'persona' });
  const differentDelivery = useWatch({ control: form.control, name: 'different_delivery_address' });
  const isCompany = persona === 'company';

  const showRegistrationError = (error: unknown) => {
    if (error instanceof ValidationError) {
      const unmapped: string[] = [];
      for (const [field, messages] of Object.entries(error.fields)) {
        const target = SERVER_FIELD_MAP[field];
        if (field === 'g-recaptcha-response')
          form.setError('root.g-recaptcha-response', {
            type: 'server',
            message: messages.join('\n'),
          });
        else if (target) form.setError(target, { type: 'server', message: messages.join('\n') });
        else unmapped.push(...messages);
      }
      const firstMapped = Object.keys(error.fields)
        .map(field => SERVER_FIELD_MAP[field])
        .find(Boolean);
      if (firstMapped) form.setFocus(firstMapped);
      if (!unmapped.length) return;
      toast({
        title: REGISTER_COPY.failed,
        description: unmapped.join(' '),
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: REGISTER_COPY.failed,
      description: error instanceof Error ? error.message : undefined,
      variant: 'destructive',
    });
  };

  const saveDetails = async (values: RegisterFormValues, session: Session) => {
    const problems: string[] = [];
    const update = toCustomerUpdate(values, countries, defaultCountry);

    if (update) {
      try {
        const { data } = await updateCustomer({
          locale,
          authToken: session.authToken,
          data: update,
        });
        initCustomerStore({
          authToken: session.authToken,
          customer: { ...session.customer, ...data },
        });
      } catch {
        problems.push(REGISTER_COPY.detailsFailed);
      }
    }

    if (values.persona === 'company' && values.business_account) {
      try {
        await sendWholesalerContactMessage({
          locale,
          data: toBusinessAccountRequest(values),
        });
      } catch {
        problems.push(REGISTER_COPY.businessAccountFailed);
      }
    }

    if (problems.length) {
      toast({ title: problems.join(' '), variant: 'destructive' });
    }
  };

  const onSubmit = form.handleSubmit(async values => {
    let session: Session;
    try {
      const captcha = '';
      const response = await register({ ...toRegisterBody(values, undefined), locale });
      await setAuthToken({ authToken: response.data.token });
      session = { authToken: response.data.token, customer: response.data.customer };
      initCustomerStore(session);
    } catch (error) {
      showRegistrationError(error);
      return;
    }

    await saveDetails(values, session);
    showRegistered();
  });

  return (
    <RegisterLayoutProvider value={layout}>
      <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors.root ?? {}} />
        <form
          noValidate
          aria-label={REGISTER_COPY.pageTitle}
          onSubmit={onSubmit}
          className={cn('flex w-full flex-col gap-6', layout === 'page' && 'mx-auto max-w-[876px]')}
        >
          <PersonaCard />
          <div className="flex flex-col gap-4 md:gap-6">
            <CredentialsCard />
            {isCompany ? (
              <CompanyCard countries={countries} />
            ) : (
              <>
                <PersonalCard />
                <BillingCard countries={countries} />
              </>
            )}
            {differentDelivery ? (
              <DeliveryCard countries={countries} withRecipient={isCompany} />
            ) : null}
            {isCompany ? <ContactCard /> : null}
          </div>
          <Consents persona={persona} />
          <div className="flex justify-center md:mt-4">
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting}
              className="h-12 w-full md:h-[52px] md:w-auto"
            >
              {t('text758')}
            </Button>
          </div>
        </form>
      </Form>
    </RegisterLayoutProvider>
  );
}
